import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  generateHarmonicPalette,
  normalizePaletteDials,
  applyContrastRemapToPaletteOrder,
  normalizePaletteColorList,
  DEFAULT_HARMONY_MODE,
  normalizeHex,
  beadColorKey,
  letterInkForHex,
  unusedPaintColor,
  MAX_PREVIEW_ROLE_COLORS,
} from "../lib/harmonicPalette";
import ColorField from "./ColorField";
import {
  generateMotifLetters,
  generateWeaveLetters,
  mapWeaveLettersToColors,
} from "../lib/weaveGenerate";
import {
  mapLettersToColors,
  patternHasContent,
  tweakPattern,
} from "../lib/tweakPattern";
import {
  DEFAULT_DISTORT_LEVEL,
  DISTORT_LEVELS,
  clampDistortLevel,
  distortPattern,
} from "../lib/distortPattern";
import { MERGE_CURVES, mergeColorsPattern } from "../lib/colorMerge";
import * as api from "../lib/api";

/** Random numeric palette parameters for a fixed harmony mode (no manual sliders). */
function randomPaletteDialsForHarmony(harmonyMode) {
  const pick = (a, b) => a + Math.random() * (b - a);
  return {
    harmonyMode,
    paletteStructure: pick(0.06, 0.78),
    temperature: pick(-0.55, 0.55),
    vibrancy: pick(-0.45, 0.55),
    spread: pick(-0.5, 0.55),
    lightnessBias: pick(-0.4, 0.45),
    chroma: pick(-0.35, 0.45),
    preserveContrast: pick(-0.25, 0.35),
  };
}

function readBoundedInt(text, min, max, fallback) {
  const parsed = parseInt(text, 10);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(min, Math.min(max, parsed));
}

function sumProgrammaticRuns(runs) {
  return Array.isArray(runs)
    ? runs.reduce((acc, r) => acc + (Number(r?.count) || 0), 0)
    : 0;
}

function normalizeProgrammaticRuns(runs, letters, segmentLength) {
  if (!Array.isArray(runs)) return null;
  const normalized = [];
  for (let i = 0; i < runs.length; i++) {
    const letter = String(runs[i]?.letter || "").toUpperCase();
    const count = Number(runs[i]?.count) || 0;
    if (!letter || count <= 0) continue;
    if (!letters.includes(letter)) continue;
    const last = normalized[normalized.length - 1];
    if (last && last.letter === letter) last.count += count;
    else normalized.push({ letter, count });
  }
  if (normalized.length === 0) return null;
  if (sumProgrammaticRuns(normalized) !== segmentLength) return null;
  return normalized;
}

/** Default when no saved palette is selected (replaces random colors). */
const NEUTRAL_PALETTE_COLORS = ["#000000", "#ffffff", "#808080"];

function neutralPaletteByLetter() {
  return {
    A: { color: NEUTRAL_PALETTE_COLORS[0] },
    B: { color: NEUTRAL_PALETTE_COLORS[1] },
    C: { color: NEUTRAL_PALETTE_COLORS[2] },
  };
}

const PatternGenerator = ({
  onConvert,
  cols,
  rows,
  pattern = [],
  savedPalettes = [],
  onPalettesChanged,
  onActivePaletteColorsChange,
  onApplySavedPalette,
  externalPaletteColors = null,
  activeColor,
  defaultColor = "#e6e6e6",
  onSelectPaintColor,
  onCommitPaintColor,
  previewAdjustEl = null,
  onOpenDissolve,
  onAdjust,
  onResetPattern,
  canReset = false,
}) => {
  const [recolorHarmonyMode] = useState(DEFAULT_HARMONY_MODE);
  const paletteSyncSigRef = useRef("");

  const rollsForHarmonicPalette = useCallback(() => ({
    ...randomPaletteDialsForHarmony(recolorHarmonyMode),
    baseColor: null,
  }), [recolorHarmonyMode]);

  // Programmatic (random repeating segment) parameters
  const [programmaticNumColorsText, setProgrammaticNumColorsText] =
    useState(String(NEUTRAL_PALETTE_COLORS.length));
  const [programmaticSegmentLengthText, setProgrammaticSegmentLengthText] =
    useState("7");
  const [programmaticVarietyMode, setProgrammaticVarietyMode] =
    useState("balanced"); // balanced | fine | bursty | chaotic | motif | wave | gradient
  const [gradientCurve, setGradientCurve] = useState("linear");
  const [gradientEaseText, setGradientEaseText] = useState("2");
  const [adjustLevel, setAdjustLevel] = useState(DEFAULT_DISTORT_LEVEL);
  // null | { mode: "runs", runs }
  const [structureRecipe, setStructureRecipe] = useState(null);
  const [, setProgrammaticLetterToColor] = useState(null); // { A: "#...", ... }
  const [programmaticPalette, setProgrammaticPalette] = useState(
    () => neutralPaletteByLetter()
  ); // { A: { color } }

  const savedColorPalettes = savedPalettes || [];
  const [generatePaletteId, setGeneratePaletteId] = useState("");
  const [isPaletteSaveModalOpen, setIsPaletteSaveModalOpen] = useState(false);
  const [paletteSaveEditId, setPaletteSaveEditId] = useState(null);
  const [paletteSaveName, setPaletteSaveName] = useState("");
  const [paletteSaveError, setPaletteSaveError] = useState("");
  const [isPaletteSaving, setIsPaletteSaving] = useState(false);

  const programmaticLetters = useMemo(() => {
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const parsed = parseInt(programmaticNumColorsText, 10);
    const n = Math.max(1, Math.min(26, Number.isFinite(parsed) ? parsed : 1));
    return alphabet.slice(0, n).split("");
  }, [programmaticNumColorsText]);

  useEffect(() => {
    setProgrammaticPalette((prev) => {
      const next = {};
      const rawDials = rollsForHarmonicPalette();
      const dialOpts = normalizePaletteDials(rawDials);
      let randoms = generateHarmonicPalette(
        programmaticLetters.length,
        rawDials
      );
      if (Math.abs(dialOpts.preserveContrast) > 0.001) {
        const prevColors = programmaticLetters.map((ltr) => {
          const c = prev?.[ltr]?.color;
          return /^#[0-9A-Fa-f]{6}$/.test(c) ? c : "#888888";
        });
        randoms = applyContrastRemapToPaletteOrder(
          prevColors,
          randoms,
          dialOpts.preserveContrast
        );
      }

      programmaticLetters.forEach((letter, idx) => {
        const existing = prev?.[letter];
        next[letter] = {
          color:
            existing?.color && /^#[0-9A-Fa-f]{6}$/.test(existing.color)
              ? existing.color
              : randoms[idx],
        };
      });

      return next;
    });
  }, [programmaticLetters, rollsForHarmonicPalette]);

  // Apply palette changes from Palettes / Library into this Colors section.
  useEffect(() => {
    const list = normalizePaletteColorList(externalPaletteColors);
    const sig = list.join(",");
    if (!sig || sig === paletteSyncSigRef.current) return;
    paletteSyncSigRef.current = sig;
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const count = Math.min(MAX_PREVIEW_ROLE_COLORS, list.length);
    const letters = alphabet.slice(0, count).split("");
    setProgrammaticNumColorsText(String(count));
    setProgrammaticPalette(() => {
      const next = {};
      letters.forEach((letter, index) => {
        next[letter] = { color: list[index] };
      });
      return next;
    });
  }, [externalPaletteColors]);

  // Keep Home's paint / Flip palette in sync with the Colors section.
  useEffect(() => {
    const colors = [];
    for (const letter of programmaticLetters) {
      const hex = normalizeHex(programmaticPalette?.[letter]?.color);
      if (hex) colors.push(hex);
    }
    const sig = colors.join(",");
    if (sig === paletteSyncSigRef.current) return;
    const externalSig = normalizePaletteColorList(externalPaletteColors).join(
      ","
    );
    // Parent just pushed a different palette; wait for the apply effect above.
    if (externalSig && externalSig !== sig) return;
    paletteSyncSigRef.current = sig;
    if (onActivePaletteColorsChange) {
      onActivePaletteColorsChange(colors.length > 0 ? colors : null);
    }
  }, [
    programmaticLetters,
    programmaticPalette,
    externalPaletteColors,
    onActivePaletteColorsChange,
  ]);

  /**
   * Generate keeps colors already on the chart; recolor replaces them with a
   * new harmonic palette.
   */
  const resolveLetterColors = (reroll) => {
    const letters = programmaticLetters;
    const rawDials = rollsForHarmonicPalette();
    const dialOpts = normalizePaletteDials(rawDials);
    let randomPalette = generateHarmonicPalette(letters.length, rawDials);
    if (Math.abs(dialOpts.preserveContrast) > 0.001) {
      const prevColors = letters.map((ltr) => {
        const c = programmaticPalette?.[ltr]?.color;
        return /^#[0-9A-Fa-f]{6}$/.test(c) ? c : "#888888";
      });
      randomPalette = applyContrastRemapToPaletteOrder(
        prevColors,
        randomPalette,
        dialOpts.preserveContrast
      );
    }
    const letterToColor = {};
    letters.forEach((letter, idx) => {
      const configured = programmaticPalette?.[letter]?.color;
      const valid = Boolean(
        configured && /^#[0-9A-Fa-f]{6}$/.test(configured)
      );
      if (valid && !reroll) {
        letterToColor[letter] = configured;
      } else {
        letterToColor[letter] = randomPalette[idx];
      }
    });
    setProgrammaticPalette((prev) => {
      const next = { ...(prev || {}) };
      letters.forEach((letter) => {
        next[letter] = {
          color: letterToColor[letter],
        };
      });
      return next;
    });
    setProgrammaticLetterToColor(letterToColor);
    return letterToColor;
  };

  const generateProgrammaticSegment = () => {
    const parsedSegmentLength = parseInt(programmaticSegmentLengthText, 10);
    const segmentLength = Math.max(
      1,
      Math.min(500, Number.isFinite(parsedSegmentLength) ? parsedSegmentLength : 1)
    );
    const letters = programmaticLetters;
    const letterToColor = resolveLetterColors(false);

    const randInt = (min, max) =>
      min + Math.floor(Math.random() * (max - min + 1));

    const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

    const shuffleInPlace = (arr) => {
      for (let i = arr.length - 1; i > 0; i--) {
        const j = randInt(0, i);
        const tmp = arr[i];
        arr[i] = arr[j];
        arr[j] = tmp;
      }
      return arr;
    };

    const countsFromCuts = (len, runCount) => {
      if (runCount <= 1) return [len];
      const cuts = new Set();
      while (cuts.size < Math.max(0, runCount - 1)) {
        cuts.add(randInt(1, len - 1));
      }
      const sortedCuts = Array.from(cuts).sort((x, y) => x - y);
      const counts = [];
      let prev = 0;
      for (let i = 0; i < sortedCuts.length; i++) {
        counts.push(sortedCuts[i] - prev);
        prev = sortedCuts[i];
      }
      counts.push(len - prev);
      return counts;
    };

    const countsGeometric = (len, minRun = 1, maxRun = 12) => {
      const counts = [];
      let remaining = len;
      while (remaining > 0) {
        // geometric-ish: many small, occasional larger
        let c = minRun;
        while (Math.random() < 0.35 && c < maxRun) c += 1;
        c = clamp(c, minRun, Math.min(maxRun, remaining));
        counts.push(c);
        remaining -= c;
      }
      return counts;
    };

    const countsMixed = (len) => {
      // Mix several strategies in one segment.
      const chunks = randInt(2, 4);
      const splits = countsFromCuts(len, chunks);
      const out = [];
      for (let i = 0; i < splits.length; i++) {
        const part = splits[i];
        const modeRoll = Math.random();
        if (modeRoll < 0.34) {
          out.push(...countsGeometric(part, 1, clamp(Math.ceil(part / 2), 3, 20)));
        } else if (modeRoll < 0.67) {
          const rc = clamp(randInt(1, part), 1, Math.min(part, 10));
          out.push(...countsFromCuts(part, rc));
        } else {
          // one big + small crumbs
          const big = clamp(randInt(1, part), 1, part);
          const rest = part - big;
          out.push(big);
          if (rest > 0) out.push(...countsGeometric(rest, 1, 6));
        }
      }
      return out;
    };

    const isTooRegular = (runs) => {
      if (!runs || runs.length < 3) return false;
      // Too many identical counts
      let sameCountStreak = 1;
      for (let i = 1; i < runs.length; i++) {
        if (runs[i].count === runs[i - 1].count) sameCountStreak += 1;
        else sameCountStreak = 1;
        if (sameCountStreak >= 4) return true;
      }
      // Detect small repeated pattern in counts (period 1-3)
      const counts = runs.map((r) => r.count);
      const maxPeriod = Math.min(3, Math.floor(counts.length / 2));
      for (let p = 1; p <= maxPeriod; p++) {
        let ok = true;
        for (let i = 0; i < counts.length; i++) {
          if (counts[i] !== counts[i % p]) {
            ok = false;
            break;
          }
        }
        if (ok) return true;
      }
      // Too few unique letters
      const lettersUsed = new Set(runs.map((r) => r.letter));
      if (lettersUsed.size <= 1 && letters.length > 1) return true;
      return false;
    };

    let runs = null;
    const buildRuns = () => {
        // Decide counts
        let counts = [];
        if (programmaticVarietyMode === "fine") {
          const runCount = clamp(
            Math.round(segmentLength * 0.75),
            2,
            segmentLength
          );
          counts = countsFromCuts(segmentLength, runCount);
          // Fine-grained still benefits from shuffling
          shuffleInPlace(counts);
        } else if (programmaticVarietyMode === "bursty") {
          counts = countsGeometric(segmentLength, 1, clamp(Math.ceil(segmentLength / 2), 4, 24));
        } else if (programmaticVarietyMode === "chaotic") {
          counts = countsMixed(segmentLength);
          shuffleInPlace(counts);
        } else {
          // balanced
          const minRuns = 2;
          const maxRuns = Math.min(segmentLength, Math.max(2, letters.length * 4));
          const a = randInt(minRuns, maxRuns);
          const b = randInt(minRuns, maxRuns);
          const runCount = clamp(Math.round((a + b) / 2), 1, segmentLength);
          counts = countsFromCuts(segmentLength, runCount);
          shuffleInPlace(counts);
        }

        // Assign letters to runs
        const allowImmediateRepeat =
          programmaticVarietyMode === "chaotic" ? Math.random() < 0.25 : false;
        const localRuns = [];
        let prevLetter = null;
        for (let i = 0; i < counts.length; i++) {
          let letter = letters[randInt(0, letters.length - 1)];
          if (letters.length > 1 && !allowImmediateRepeat) {
            let guard = 0;
            while (letter === prevLetter && guard < 10) {
              letter = letters[randInt(0, letters.length - 1)];
              guard += 1;
            }
          }
          localRuns.push({ letter, count: counts[i] });
          prevLetter = letter;
        }
        return localRuns;
      };

      // Re-roll a few times if the recipe looks too regular.
      runs = null;
      const attempts = programmaticVarietyMode === "chaotic" ? 12 : 8;
      for (let attempt = 0; attempt < attempts; attempt++) {
        const candidate = buildRuns();
        if (!isTooRegular(candidate)) {
          runs = candidate;
          break;
        }
        runs = candidate;
      }

    const segment = [];
    runs.forEach(({ letter, count }) => {
      for (let i = 0; i < count; i++) {
        segment.push(letterToColor[letter]);
      }
    });

    setStructureRecipe({ mode: "runs", runs });

    return segment.slice(0, segmentLength);
  };

  // Helper: Convert HSL to hex (unused - kept for potential future use)
  // const hslToHex = (h, s, l) => {
  //   l /= 100;
  //   const a = (s * Math.min(l, 1 - l)) / 100;
  //   const f = (n) => {
  //     const k = (n + h / 30) % 12;
  //     const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
  //     return Math.round(255 * color)
  //       .toString(16)
  //       .padStart(2, "0");
  //   };
  //   return `#${f(0)}${f(8)}${f(4)}`;
  // };

  // Helper: Convert hex to HSL (unused - kept for potential future use)
  // const hexToHsl = (hex) => {
  //   const r = parseInt(hex.slice(1, 3), 16) / 255;
  //   const g = parseInt(hex.slice(3, 5), 16) / 255;
  //   const b = parseInt(hex.slice(5, 7), 16) / 255;
  //   const max = Math.max(r, g, b);
  //   const min = Math.min(r, g, b);
  //   let h,
  //     s,
  //     l = (max + min) / 2;
  //   if (max === min) {
  //     h = s = 0;
  //   } else {
  //     const d = max - min;
  //     s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  //     switch (max) {
  //       case r:
  //         h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  //         break;
  //       case g:
  //         h = ((b - r) / d + 2) / 6;
  //         break;
  //       case b:
  //         h = ((r - g) / d + 4) / 6;
  //         break;
  //       default:
  //         h = 0;
  //         break;
  //     }
  //   }
  //   return [h * 360, s * 100, l * 100];
  // };

  const weavePaintColors = () => {
    const colors = getGeneratorPaletteColors();
    const colorA = colors[0] || "#000000";
    const colorB = colors[1] || colors[0] || "#ffffff";
    return { colorA, colorB };
  };

  const applyLetterPattern = (mode, generate) => {
    const { letters, meta } = generate({ cols, rows });
    const { colorA, colorB } = weavePaintColors();
    // Keep A/B paint slots aligned with the two letter roles.
    setProgrammaticNumColorsText("2");
    setProgrammaticPalette({
      A: { color: colorA },
      B: { color: colorB },
    });
    setProgrammaticLetterToColor({ A: colorA, B: colorB });
    setStructureRecipe({ mode, letters, meta });
    onConvert(mapWeaveLettersToColors(letters, colorA, colorB));
  };

  const applyMutatedPattern = (mode, mutate, extra = {}) => {
    if (!patternHasContent(pattern, defaultColor)) return;
    const result = mutate({
      pattern,
      cols,
      rows,
      defaultColor,
      ...extra,
    });
    if (!result) return;
    const lettersUsed = Object.keys(result.letterToColor).sort();
    const nextPalette = {};
    lettersUsed.forEach((letter) => {
      nextPalette[letter] = { color: result.letterToColor[letter] };
    });
    setProgrammaticNumColorsText(String(Math.max(1, lettersUsed.length)));
    setProgrammaticPalette(nextPalette);
    setProgrammaticLetterToColor({ ...result.letterToColor });
    setStructureRecipe({
      mode,
      letters: result.letters,
      meta: result.meta,
    });
    (onAdjust || onConvert)(result.pattern);
  };

  const level = clampDistortLevel(adjustLevel);
  const levelInfo =
    DISTORT_LEVELS.find((item) => item.id === level) || DISTORT_LEVELS[0];
  const applyTweakPattern = () =>
    applyMutatedPattern("tweak", tweakPattern, { level });
  const applyDistortPattern = () =>
    applyMutatedPattern("distort", distortPattern, { level });

  const handleConvert = () => {
    if (!cols || !rows) return;
    if (programmaticVarietyMode === "gradient") {
      const { colorA, colorB } = weavePaintColors();
      const easePower = parseFloat(gradientEaseText);
      const result = mergeColorsPattern({
        color1: colorA,
        color2: colorB,
        cols,
        rows,
        curve: gradientCurve,
        easePower: Number.isFinite(easePower) ? easePower : 2,
      });
      setStructureRecipe(null);
      onConvert(result.pattern);
      return;
    }
    if (programmaticVarietyMode === "motif") {
      applyLetterPattern("motif", generateMotifLetters);
      return;
    }
    if (programmaticVarietyMode === "wave") {
      applyLetterPattern("wave", generateWeaveLetters);
      return;
    }
    onConvert(applyRepetition(generateProgrammaticSegment()));
  };

  /** Tile the generated segment to fill the grid. */
  const applyRepetition = (basePattern) => {
    if (!basePattern || basePattern.length === 0) return basePattern;

    const totalBeads = cols * rows;
    const repeatedPattern = [];
    const filteredPattern = basePattern.filter(
      (c) => c !== null && c !== undefined
    );

    if (filteredPattern.length === 0) return basePattern;

    while (repeatedPattern.length < totalBeads) {
      for (
        let i = 0;
        i < filteredPattern.length && repeatedPattern.length < totalBeads;
        i++
      ) {
        repeatedPattern.push(filteredPattern[i]);
      }
    }

    return repeatedPattern.slice(0, totalBeads);
  };

  const recolorProgrammaticSameRecipe = () => {
    if (!cols || !rows || !structureRecipe) return;

    if (
      (structureRecipe.mode === "motif" || structureRecipe.mode === "wave") &&
      Array.isArray(structureRecipe.letters)
    ) {
      const letterToColor = resolveLetterColors(true);
      const colorA = letterToColor.A || "#000000";
      const colorB = letterToColor.B || colorA;
      setProgrammaticLetterToColor({ A: colorA, B: colorB });
      onConvert(
        mapWeaveLettersToColors(structureRecipe.letters, colorA, colorB)
      );
      return;
    }

    if (
      (structureRecipe.mode === "tweak" ||
        structureRecipe.mode === "distort") &&
      Array.isArray(structureRecipe.letters)
    ) {
      const letterToColor = resolveLetterColors(true);
      onConvert(
        mapLettersToColors(structureRecipe.letters, letterToColor, defaultColor)
      );
      return;
    }

    const parsedSegmentLength = parseInt(programmaticSegmentLengthText, 10);
    const segmentLength = Math.max(
      1,
      Math.min(500, Number.isFinite(parsedSegmentLength) ? parsedSegmentLength : 1)
    );
    const runs = normalizeProgrammaticRuns(
      structureRecipe.runs,
      programmaticLetters,
      segmentLength
    );
    if (!runs) return;
    const letterToColor = resolveLetterColors(true);

    const segment = [];
    runs.forEach(({ letter, count }) => {
      for (let i = 0; i < count; i++) {
        segment.push(letterToColor[letter]);
      }
    });

    setStructureRecipe({ mode: "runs", runs });
    onConvert(applyRepetition(segment.slice(0, segmentLength)));
  };

  /** Generator letter colors, in letter order, deduped for saving. */
  const getGeneratorPaletteColors = () => {
    const out = [];
    const seen = new Set();
    for (const letter of programmaticLetters) {
      const raw = programmaticPalette?.[letter]?.color;
      const hex = normalizeHex(raw);
      if (!hex) continue;
      if (seen.has(hex)) continue;
      seen.add(hex);
      out.push(hex);
    }
    return out;
  };

  /** Seeds the generator letters from a saved palette: one letter per color. */
  const applySavedPaletteToGenerator = (entry) => {
    const colors = normalizePaletteColorList(entry?.colors);
    if (colors.length === 0) return;
    const count = Math.min(MAX_PREVIEW_ROLE_COLORS, colors.length);
    const applied = colors.slice(0, count);
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".slice(0, count).split("");
    const sig = applied.join(",");

    // Update paint slots locally and recolor the canvas. Set the sync sig
    // after both so the external-palette effect does not skip the paint update.
    setProgrammaticNumColorsText(String(count));
    setProgrammaticPalette(() => {
      const next = {};
      letters.forEach((letter, index) => {
        next[letter] = { color: applied[index] };
      });
      return next;
    });
    paletteSyncSigRef.current = sig;

    if (onApplySavedPalette) {
      onApplySavedPalette(applied);
    } else if (onActivePaletteColorsChange) {
      onActivePaletteColorsChange(applied);
    }
  };

  const handleAddPaintColor = () => {
    if (programmaticLetters.length >= MAX_PREVIEW_ROLE_COLORS) return;
    const existing = getGeneratorPaletteColors();
    const nextHex = unusedPaintColor(existing, defaultColor);
    if (!nextHex) return;
    const nextCount = programmaticLetters.length + 1;
    const letter = "ABCDEFGHIJKLMNOPQRSTUVWXYZ"[nextCount - 1];
    setProgrammaticNumColorsText(String(nextCount));
    setProgrammaticPalette((prev) => ({
      ...(prev || {}),
      [letter]: { color: nextHex },
    }));
    if (onSelectPaintColor) onSelectPaintColor(nextHex);
  };

  const handleRemovePaintLetter = (letter) => {
    if (programmaticLetters.length <= 1) return;
    const remaining = programmaticLetters
      .filter((item) => item !== letter)
      .map((item) => normalizeHex(programmaticPalette?.[item]?.color))
      .filter(Boolean);
    if (remaining.length === 0) return;
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const letters = alphabet.slice(0, remaining.length).split("");
    const sig = remaining.join(",");
    paletteSyncSigRef.current = sig;
    setProgrammaticNumColorsText(String(remaining.length));
    setProgrammaticPalette(() => {
      const next = {};
      letters.forEach((item, index) => {
        next[item] = { color: remaining[index] };
      });
      return next;
    });
    if (onActivePaletteColorsChange) {
      onActivePaletteColorsChange(remaining);
    }
    if (onSelectPaintColor) {
      const removedKey = beadColorKey(
        programmaticPalette?.[letter]?.color
      );
      const activeKey = beadColorKey(activeColor);
      if (activeKey && removedKey && activeKey === removedKey) {
        onSelectPaintColor(remaining[0]);
      }
    }
  };

  const handleCommitLetterColor = (letter, fromColor, toColor) => {
    const nextHex = normalizeHex(toColor);
    if (!nextHex) return;
    setProgrammaticPalette((prev) => ({
      ...(prev || {}),
      [letter]: { color: nextHex },
    }));
    if (onCommitPaintColor) {
      onCommitPaintColor(fromColor, nextHex);
    }
  };

  const handleFillPattern = (color) => {
    if (!cols || !rows || !onConvert) return;
    const hex = normalizeHex(color) || color;
    if (!hex) return;
    const totalBeads = cols * rows;
    onConvert(Array.from({ length: totalBeads }, () => hex));
    if (onSelectPaintColor) onSelectPaintColor(hex);
  };

  const openPaletteSaveModal = ({ editId, name }) => {
    setPaletteSaveEditId(editId ?? null);
    setPaletteSaveName(name || "");
    setPaletteSaveError("");
    setIsPaletteSaveModalOpen(true);
  };

  const handleSaveColorPaletteSubmit = async () => {
    const trimmed = paletteSaveName.trim();
    if (!trimmed) {
      setPaletteSaveError("Please enter a palette name.");
      return;
    }
    const colors = getGeneratorPaletteColors();
    if (colors.length === 0) {
      setPaletteSaveError(
        "No colors to save — set the generator colors first."
      );
      return;
    }
    setIsPaletteSaving(true);
    setPaletteSaveError("");
    try {
      if (paletteSaveEditId != null) {
        await api.updateColorPalette(paletteSaveEditId, {
          name: trimmed,
          colors,
        });
        if (onPalettesChanged) await onPalettesChanged();
        setGeneratePaletteId(String(paletteSaveEditId));
      } else {
        const created = await api.createColorPalette({ name: trimmed, colors });
        if (onPalettesChanged) await onPalettesChanged();
        if (created?.id != null) {
          setGeneratePaletteId(String(created.id));
        }
      }
      setPaletteSaveName("");
      setPaletteSaveEditId(null);
      setIsPaletteSaveModalOpen(false);
    } catch (err) {
      console.error(err);
      setPaletteSaveError(err.message || "Failed to save color palette.");
    } finally {
      setIsPaletteSaving(false);
    }
  };

  const generatePaletteEntry =
    savedColorPalettes.find((p) => String(p.id) === generatePaletteId) || null;

  return (
    <>
    <div className="pattern-generator-content">
      <div className="pattern-generator-content__inner">
          <section className="pg-section">
            <h3 className="pg-section__title">Colors</h3>
            <div className="pg-field-row">
            <div className="detail-field">
              <label htmlFor="generate-saved-palette">
                Start from a saved palette
              </label>
              <select
                id="generate-saved-palette"
                className="select-input"
                value={generatePaletteId}
                disabled={savedColorPalettes.length === 0}
                onChange={(e) => {
                  const id = e.target.value;
                  setGeneratePaletteId(id);
                  if (!id) {
                    applySavedPaletteToGenerator({
                      colors: NEUTRAL_PALETTE_COLORS,
                    });
                    return;
                  }
                  const entry = savedColorPalettes.find(
                    (p) => String(p.id) === id
                  );
                  if (entry) {
                    applySavedPaletteToGenerator(entry);
                  }
                }}
              >
                <option value="">
                  {savedColorPalettes.length === 0
                    ? "No saved palettes yet"
                    : "None — black, white, gray"}
                </option>
                {savedColorPalettes.map((p) => (
                  <option key={p.id} value={String(p.id)}>
                    {p.name} ({p.colors?.length ?? 0} colors)
                  </option>
                ))}
              </select>
            </div>
            </div>
          <div className="pattern-gen-top-palette">
            <div className="morse-code-colors">
              <div className="detail-field" style={{ flex: "1 1 100%" }}>
                <div
                  className="pattern-role-colors pattern-gen-paint-palette"
                  role="group"
                  aria-label="Paint colors"
                >
                  {programmaticLetters.map((letter) => {
                    const entry = programmaticPalette?.[letter] || {};
                    const colorValue =
                      entry.color && /^#[0-9A-Fa-f]{6}$/.test(entry.color)
                        ? entry.color
                        : "#000000";
                    const hex = normalizeHex(colorValue) || colorValue;
                    const isActive =
                      beadColorKey(activeColor) === beadColorKey(colorValue);
                    const ink = letterInkForHex(colorValue);
                    return (
                      <div
                        key={`prog-top-${letter}`}
                        className={`pattern-role-color${
                          isActive ? " is-active" : ""
                        }`}
                        onClick={() =>
                          onSelectPaintColor && onSelectPaintColor(colorValue)
                        }
                      >
                        <div className="pattern-role-color__controls">
                          <button
                            type="button"
                            className="pattern-role-color__letter"
                            style={{
                              backgroundColor: colorValue,
                              color: ink,
                            }}
                            aria-label={`Paint with ${letter}`}
                            aria-pressed={isActive}
                            title={`Paint with ${letter}`}
                            onClick={() =>
                              onSelectPaintColor &&
                              onSelectPaintColor(colorValue)
                            }
                          >
                            {letter}
                          </button>
                          <ColorField
                            key={`${letter}-${hex}`}
                            color={hex}
                            colorLabel={`Recolor ${letter}`}
                            hexLabel={`${letter} hex`}
                            className={`color-input-group${
                              isActive ? " is-active" : ""
                            }`}
                            onGroupClick={() =>
                              onSelectPaintColor &&
                              onSelectPaintColor(colorValue)
                            }
                            onCommit={(nextHex) =>
                              handleCommitLetterColor(
                                letter,
                                colorValue,
                                nextHex
                              )
                            }
                          />
                          <button
                            type="button"
                            className="button button--compact"
                            aria-label={`Remove ${letter}`}
                            title={`Remove ${letter}`}
                            disabled={programmaticLetters.length <= 1}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleRemovePaintLetter(letter);
                            }}
                          >
                            Remove
                          </button>
                        </div>
                        <button
                          type="button"
                          className="pattern-role-color__fill"
                          aria-label={`Fill pattern with ${letter}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleFillPattern(colorValue);
                          }}
                        >
                          Fill
                        </button>
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    className="button button--compact"
                    onClick={handleAddPaintColor}
                    disabled={
                      programmaticLetters.length >= MAX_PREVIEW_ROLE_COLORS
                    }
                  >
                    Add Color
                  </button>
                  <div
                    className={`pattern-role-color pattern-role-color--empty${
                      beadColorKey(activeColor) === beadColorKey(defaultColor)
                        ? " is-active"
                        : ""
                    }`}
                  >
                    <button
                      type="button"
                      className={`color-palette-mini__swatch is-empty${
                        beadColorKey(activeColor) === beadColorKey(defaultColor)
                          ? " is-active"
                          : ""
                      }`}
                      style={{ backgroundColor: defaultColor }}
                      onClick={() =>
                        onSelectPaintColor && onSelectPaintColor(defaultColor)
                      }
                      aria-label="Paint with Empty"
                      aria-pressed={
                        beadColorKey(activeColor) === beadColorKey(defaultColor)
                      }
                      title="Empty"
                    >
                      <span className="color-palette-mini__label">Empty</span>
                    </button>
                    <button
                      type="button"
                      className="pattern-role-color__fill"
                      aria-label="Fill pattern with Empty"
                      onClick={() => handleFillPattern(defaultColor)}
                    >
                      Fill
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
            <div className="pg-section__actions">
              <button
                type="button"
                className="button"
                onClick={() => openPaletteSaveModal({ editId: null })}
              >
                Save Palette
              </button>
              {generatePaletteEntry && (
                <button
                  type="button"
                  className="button"
                  onClick={() =>
                    openPaletteSaveModal({
                      editId: generatePaletteEntry.id,
                      name: generatePaletteEntry.name || "",
                    })
                  }
                >
                  Update “{generatePaletteEntry.name}”…
                </button>
              )}
            </div>
          </section>
            <section className="pg-section">
              <h3 className="pg-section__title">Auto-Generate New Pattern</h3>
              <div className="morse-code-options">
                <div className="detail-field">
                  <label htmlFor="programmatic-variety">Variety</label>
                  <select
                    id="programmatic-variety"
                    value={programmaticVarietyMode}
                    onChange={(e) => setProgrammaticVarietyMode(e.target.value)}
                    className="select-input"
                  >
                    <option value="balanced">Balanced</option>
                    <option value="fine">Fine-grained</option>
                    <option value="bursty">Bursty</option>
                    <option value="chaotic">Chaotic</option>
                    <option value="motif">Motif</option>
                    <option value="wave">Wave</option>
                    <option value="gradient">Gradient</option>
                  </select>
                </div>

                {programmaticVarietyMode === "gradient" && (
                  <div className="detail-field">
                    <label htmlFor="gradient-curve">Curve</label>
                    <select
                      id="gradient-curve"
                      value={gradientCurve}
                      onChange={(event) => setGradientCurve(event.target.value)}
                      className="select-input"
                    >
                      {MERGE_CURVES.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {programmaticVarietyMode === "gradient" &&
                  gradientCurve === "ease" && (
                  <div className="detail-field">
                    <label htmlFor="gradient-ease">Easing power</label>
                    <input
                      id="gradient-ease"
                      type="text"
                      inputMode="decimal"
                      value={gradientEaseText}
                      onChange={(event) => setGradientEaseText(event.target.value)}
                      className="color-text-input"
                    />
                  </div>
                )}

                {programmaticVarietyMode !== "motif" &&
                  programmaticVarietyMode !== "wave" &&
                  programmaticVarietyMode !== "gradient" && (
                  <div className="detail-field">
                    <label htmlFor="programmatic-segment-length">
                      Repeating Segment Length
                    </label>
                    <input
                      id="programmatic-segment-length"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={programmaticSegmentLengthText}
                      onChange={(e) => {
                        const next = e.target.value;
                        if (next === "" || /^[0-9]+$/.test(next)) {
                          setProgrammaticSegmentLengthText(next);
                        }
                      }}
                      onBlur={() => {
                        setProgrammaticSegmentLengthText(
                          String(
                            readBoundedInt(
                              programmaticSegmentLengthText,
                              1,
                              500,
                              7
                            )
                          )
                        );
                      }}
                      className="color-text-input"
                    />
                  </div>
                )}
              </div>
              <div className="pg-section__actions">
                <button
                  type="button"
                  className="button button--primary"
                  onClick={handleConvert}
                >
                  Generate Pattern
                </button>
                <button
                  type="button"
                  className="button"
                  disabled={!structureRecipe}
                  onClick={recolorProgrammaticSameRecipe}
                  title="Keeps the current letter recipe; picks a new harmonic palette."
                >
                  Recolor (same recipe)
                </button>
              </div>
            </section>
      </div>
    </div>

    {previewAdjustEl &&
      createPortal(
        <div className="preview-adjust__tools">
          <div className="preview-adjust__row">
            <button
              type="button"
              className="button"
              onClick={applyTweakPattern}
              disabled={!patternHasContent(pattern, defaultColor)}
              title={`Level ${level}: ${
                level === 1 ? "one calm change" : `${level} calm changes`
              } to the pattern already on the chart.`}
            >
              Tweak
            </button>
            <button
              type="button"
              className="button"
              onClick={applyDistortPattern}
              disabled={!patternHasContent(pattern, defaultColor)}
              title={`Level ${level}: ${levelInfo.detail}`}
            >
              Distort
            </button>
            <button
              type="button"
              className="button"
              onClick={onOpenDissolve}
            >
              Dissolve
            </button>
            <div className="preview-adjust__level">
              <label htmlFor="adjust-level">Level</label>
              <input
                id="adjust-level"
                type="range"
                className="slider"
                min={DISTORT_LEVELS[0].id}
                max={DISTORT_LEVELS[DISTORT_LEVELS.length - 1].id}
                step="1"
                value={level}
                aria-valuetext={levelInfo.label}
                onChange={(e) =>
                  setAdjustLevel(clampDistortLevel(e.target.value))
                }
              />
              <span className="preview-adjust__level-value">{levelInfo.label}</span>
            </div>
          </div>
          <button
            type="button"
            className="button"
            onClick={onResetPattern}
            disabled={!canReset}
            title="Restores the pattern from before Tweak, Distort, or Dissolve."
          >
            Reset
          </button>
        </div>,
        previewAdjustEl
      )}

    {isPaletteSaveModalOpen && (
      <div
        className="modal-overlay"
        onClick={() => {
          if (!isPaletteSaving) {
            setIsPaletteSaveModalOpen(false);
            setPaletteSaveEditId(null);
            setPaletteSaveError("");
          }
        }}
      >
        <div
          className="modal"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <h2>
            {paletteSaveEditId != null
              ? "Update Color Palette"
              : "Save Color Palette"}
          </h2>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              handleSaveColorPaletteSubmit();
            }}
            className="modal-form"
          >
            <label htmlFor="color-palette-name">Palette Name</label>
            <input
              id="color-palette-name"
              type="text"
              value={paletteSaveName}
              onChange={(event) => setPaletteSaveName(event.target.value)}
              placeholder="e.g. desert-sunset"
              autoFocus
            />
            {paletteSaveError && (
              <p className="modal-error">{paletteSaveError}</p>
            )}
            <div className="modal-actions">
              <button
                type="submit"
                className="button button--primary"
                disabled={isPaletteSaving}
              >
                {isPaletteSaving
                  ? "Saving…"
                  : paletteSaveEditId != null
                  ? "Update"
                  : "Save"}
              </button>
              <button
                type="button"
                className="button"
                onClick={() => {
                  if (!isPaletteSaving) {
                    setIsPaletteSaveModalOpen(false);
                    setPaletteSaveEditId(null);
                    setPaletteSaveError("");
                  }
                }}
                disabled={isPaletteSaving}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
    </>
  );
};

export default PatternGenerator;
